/** "Roughly what did you weigh around <month>?" (card 016 DV-22, OD-01): a gentle, skippable card on Today and
 * Health → Body, shown only while the server offers a prompt (`GET /health/weight` → `prompt`: an open gap of more
 * than 30 days after the last weigh-in, or no weigh-in yet; never for a gap with weigh-ins on both sides, D-153).
 * The answer is stored as an approximate weigh-in dated in that month (`POST /health/body`, `approximate: true`); a
 * real scale reading on the same day beats it. Skip is remembered on the server for this gap. */
import { useState, type FormEvent } from "react";
import { useLogBody, useSkipWeightPrompt, useWeight } from "@/api/hooks";
import type { WeightPrompt as Prompt } from "@/api/schemas";
import { useToast } from "./Toast";
import { Card } from "./ui";
import { day } from "@/lib/format";

/** "September" (or "September 2025" when it is not this year's). */
export function promptMonth(p: Prompt, today: string): string {
  const d = new Date(`${p.month}-01T00:00:00Z`);
  const name = d.toLocaleString("en-GB", { month: "long", timeZone: "UTC" });
  return p.month.slice(0, 4) === today.slice(0, 4) ? name : `${name} ${p.month.slice(0, 4)}`;
}

export function promptQuestion(p: Prompt, today: string): string {
  return p.since == null
    ? "Roughly what do you weigh?"
    : `Roughly what did you weigh around ${promptMonth(p, today)}?`;
}

export function WeightPrompt({ today }: { today: string }) {
  const q = useWeight(1);
  const log = useLogBody();
  const skip = useSkipWeightPrompt();
  const toast = useToast();
  const [w, setW] = useState("");
  const p = q.data?.prompt;
  if (!p) return null;
  const prompt = p;
  const busy = log.isPending || skip.isPending;
  async function submit(e: FormEvent) {
    e.preventDefault();
    const v = Number(w.replace(",", "."));
    if (!(v >= 25 && v <= 300)) return toast.error("Weight must be 25–300 kg"); // as the server (BodyIn)
    try {
      await log.mutateAsync({ weight_kg: v, measured_at: `${prompt.day}T12:00:00`, approximate: true });
      toast.ok(`Saved about ${v} kg`);
      setW("");
    } catch (x) {
      toast.error(x);
    }
  }
  async function later() {
    try {
      await skip.mutateAsync();
    } catch (x) {
      toast.error(x);
    }
  }
  return (
    <Card title="Your weight">
      <form className="stack" style={{ gap: 8 }} onSubmit={submit} data-testid="weight-prompt">
        <p style={{ margin: 0 }}>
          <b>{promptQuestion(prompt, today)}</b>
        </p>
        <p className="muted" style={{ margin: 0 }}>
          {prompt.since ? `No weigh-in since ${day(prompt.since)}.` : "No weigh-in yet."} A rough number is
          fine: it is marked as approximate, the race preparation index uses it, and a scale reading replaces
          it.
        </p>
        <div className="row" style={{ flexWrap: "nowrap" }}>
          <input
            inputMode="decimal"
            placeholder="kg"
            value={w}
            onChange={(e) => setW(e.target.value)}
            aria-label="Approximate weight in kg"
          />
          <button className="primary" disabled={busy}>
            Save
          </button>
          <button type="button" onClick={later} disabled={busy}>
            Skip
          </button>
        </div>
      </form>
    </Card>
  );
}
